import React, { useState, memo, useEffect, useMemo } from "react";
import { getTeamLogoUrl } from "../utils/helpers";
import { listOfTeams } from "../teams/team";
import "../scenario.css";
import { FlaskConical, PencilSparkles } from 'lucide-react';

const WHITE_LOGO = new Set(["Kansas State", "TCU", "Georgia Tech"]);

const REG_LOGO = new Set([
    "UCLA",
    "Florida",
    "Wake Forest",
    "Fresno State",
    "Oklahoma State",
    "West Virginia",
]);

const SWAP_COLORS = new Set([
    "Miami",
    "Texas Tech",
    "USC",
    "UCLA",
    "Oklahoma State",
    "Tulane",
    "Colorado",
    "UTEP",
    "SMU"
]);

const ScenarioEditor = ({ games = [], change, teams = [], percent = {}, simResults, setMSwitch, isMobile }) => {
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [showTeams, setShowTeams] = useState(1);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedWeeks, setSelectedWeeks] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [currentSelectedTeam, setCurrentSelectedTeam] = useState(null);
    const [step, setStep] = useState(1);
    const [filterType, setFilterType] = useState("teams");



    const toggleTeam = (teamName) => {
        setStep(1);
        setSelectedTeams((prev) =>
            prev.includes(teamName)
                ? prev.filter((t) => t !== teamName)
                : [...prev, teamName]
        );
    };

    const toggleWeek = (weekName) => {
        setStep(1);
        setSelectedWeeks((prev) =>
            prev.includes(weekName)
                ? prev.filter((t) => t !== weekName)
                : [...prev, weekName]
        );
    };


    // Aggregate and sort games for selected teams by excitement score
    const excitementSortedGames = useMemo(() => {
        // Collect active selected team names
        const activeTeamNames = new Set(
            selectedTeams.length > 0
                ? selectedTeams
                : currentSelectedTeam?.team
                    ? [currentSelectedTeam.team]
                    : []
        );

        if (activeTeamNames.size === 0 && selectedWeeks.length == 0) return [];

        const seenGameIds = new Set();
        const collectedGames = [];

        // Extract all games belonging to selected teams
        console.log(teams);
        teams.forEach((t) => {
            //if (!activeTeamNames.has(t.team)) return;

            (t.LossesTeams || []).forEach((entry) => {
                const game = entry.game;
                
                if (!game || game.completed || game.certain) return;


                const gameId = game.id || `${game.homeTeam}-${game.awayTeam}`;
                if (seenGameIds.has(gameId)) return;
                if (!activeTeamNames.has(t.team) && !game.manual && !selectedWeeks.includes(game.week)) return;

                seenGameIds.add(gameId);

                // Win probabilities (0.0 to 1.0)
                const homeProb = game.probability ?? 0.5;
                const awayProb = 1 - homeProb;

                // 1. Closeness Metric (1 = 50/50 tossup, 0 = 100/0 blowout)
                const closeness = 1 - Math.abs(homeProb - awayProb);

                // 2. Playoff Stakes Metric (Combined playoff odds of both teams)
                const homePlayoffOdds = (game.homeAvg || 0) / 100;
                const awayPlayoffOdds = (game.awayAvg || 0) / 100;
                const stakes = game.homeAvg + game.awayAvg;

                // Composite Excitement Rating (Closeness weighted with stakes factor)
                const excitementScore = closeness * 0.5 + stakes * 0.5;

                collectedGames.push({
                    entry,
                    game,
                    excitementScore,
                    homeProb,
                    awayProb
                });
            });
        });

        // Sort descending by calculated excitement score
        return collectedGames.sort((a, b) => {
            // 1. Sort completed games first (manual: false before true)
            if (a.game.manual !== b.game.manual) {
                return a.game.manual ? 1 : -1;
            }

            // 2. Move Week 14 games to the very end
            const isAWeek14 = a.game.week === 14;
            const isBWeek14 = b.game.week === 14;
            if (isAWeek14 !== isBWeek14) {
                return isAWeek14 ? -1 : 1; // Put week 14 (true) AFTER non-week 14 (false)
            }

            // 3. Secondary sort by excitementScore (descending)
            return a.excitementScore - b.excitementScore;
        });
    }, [teams, selectedTeams, currentSelectedTeam, percent]);


    useEffect(() => {
        if (step == 1) {
            document.querySelector('#scenario-editor')?.scrollIntoView({ behavior: 'smooth' });
        }
        if (step == 2) {
            document.querySelector('#scenario-details')?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [step]);

    const startWeek = 7;
    const endWeek = 14;

    return (
        <div id="scenario-editor" className="scenario-editor">
            {filterType == "teams" && <><h1>Select Teams</h1>
                <p>Pick the teams that you want to include in the scenario, then click Make Predictions.</p>

                <div className="scenario-team-selection">
                    {teams.map((team, index) => {
                        const mainColor = SWAP_COLORS.has(team.team)
                            ? team.alternateColor
                            : team.color;
                        const altColor = SWAP_COLORS.has(team.team)
                            ? team.color
                            : team.alternateColor;
                        const isSelected = selectedTeams.includes(team.team);

                        if (!isSelected && index > showTeams * 24 - 1) {
                            return null;
                        }
                        return (
                            <div key={team.team} onClick={() => toggleTeam(team.team)} className="scenario-team-container">
                                <div
                                    className="scenario-team"

                                    style={{
                                        //order: isSelected ? 0 : 1,
                                        boxShadow: isSelected
                                            ? `0 0 0 2px ${altColor || "#007ac2"} inset`
                                            : `0 0 0 2px #ccc inset`,
                                        borderColor: isSelected
                                            ? mainColor || "transparent"
                                            : "var(--text)",
                                        background: isSelected
                                            ? `linear-gradient(45deg, color-mix(in srgb, ${mainColor} 5%, white) 0%, color-mix(in srgb, ${mainColor} 85%, black) 18%, color-mix(in srgb, ${mainColor} 65%, white) 38%, color-mix(in srgb, ${mainColor} 90%, black) 58%, color-mix(in srgb, ${mainColor} 70%, black) 78%, color-mix(in srgb, ${mainColor} 45%, white) 100%)`
                                            : "var(--text)"
                                    }}
                                >
                                    <img
                                        style={{
                                            filter:
                                                WHITE_LOGO.has(team.team) && isSelected
                                                    ? "brightness(0) invert(1)"
                                                    : "none"
                                        }}
                                        src={getTeamLogoUrl(
                                            listOfTeams[team.team],
                                            REG_LOGO.has(team.team) ||
                                            WHITE_LOGO.has(team.team) ||
                                            !isSelected
                                        )}
                                        alt={team.team}
                                    />
                                </div>
                                <div className="team-name">{team.team}</div>
                            </div>
                        );
                    })}
                </div>
            
            <div className="">
                    {showTeams < 10 && <p style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setShowTeams(10)}>
                        Show All Teams
                    </p>}
                    {showTeams == 10 && <p style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setShowTeams(1)}>
                        Hide Teams
                    </p>}
                </div>
            </>}
            {filterType == "weeks" && <><h1>Select Weeks</h1>
                <p>Pick the weeks that you want to include in the scenario, then click Make Predictions.</p>

                <div className="scenario-team-selection">
                    {Array.from(
                        { length: Math.max(0, endWeek - startWeek + 1) },
                        (_, i) => startWeek + i
                    ).map((week) => {
                        const mainColor = "var(--medium)";
                        const altColor = "white";
                        const isSelected = selectedWeeks.includes(week);

                        return (
                            <div key={week} onClick={() => toggleWeek(week)} className="scenario-team-container">
                                <div
                                    className="scenario-team"

                                    style={{
                                        //order: isSelected ? 0 : 1,
                                        fontSize: '24px',
                                        fontWeight: '800',
                                        boxShadow: isSelected
                                            ? `0 0 0 2px ${altColor || "#007ac2"} inset`
                                            : `0 0 0 2px #ccc inset`,
                                        borderColor: isSelected
                                            ? mainColor || "transparent"
                                            : "var(--text)",
                                        color: isSelected
                                            ? altColor || "transparent"
                                            : mainColor,
                                        background: isSelected
                                            ? `linear-gradient(45deg, color-mix(in srgb, ${mainColor} 5%, white) 0%, color-mix(in srgb, ${mainColor} 85%, black) 18%, color-mix(in srgb, ${mainColor} 65%, white) 38%, color-mix(in srgb, ${mainColor} 90%, black) 58%, color-mix(in srgb, ${mainColor} 70%, black) 78%, color-mix(in srgb, ${mainColor} 45%, white) 100%)`
                                            : "var(--text)"
                                    }}
                                >
                                    {week}
                                </div>
                                <div className="team-name">Week {week}</div>
                            </div>
                        );
                    })}
                </div>
            </>}
            {/*<div className="butts">
            <div id="show-more" className="secret-butt" onClick={() => setShowTeams((prev) => prev + 1)}>
                    {showTeams < 6 && (
                        <span>Show More Teams</span>
                    )}
            </div>
            <div id="show-less" className="secret-butt" onClick={() => setShowTeams((prev) => prev - 1)}>
                    {showTeams > 1 && (
                        <span>Show Less Teams</span>
                    )}
            </div>
            </div>*/}
            <br />
            <br />
            <br />


            {excitementSortedGames.length > 0 && step == 2 && (
                <div id="scenario-details" className="scenario-details">
                    <h1>
                        Make Predictions
                    </h1>
                    <p>One game at a time, pick the team you think will win. Run Simulations to see the new projections.</p>
                    {excitementSortedGames.filter(g => !g.game.manual).length == 0 && <div class="run-sim-special-butt"
                        onClick={() => {
                            simResults.runSimulation?.(250);
                            setMSwitch(false);
                        }}
                    ><FlaskConical size={24} strokeWidth={2} /> &nbsp; Run Simulations</div>}
                    {excitementSortedGames.filter(g => !g.game.manual).length > 0 && <div className="bar">
                        {excitementSortedGames.map(entry => {
                            return (
                                <div className={`bar-piece ${entry.game.manual && "manual"}`}>
                                </div>
                            )
                        })}

                    </div>}
                    {excitementSortedGames.map(({ entry, game }, index) => {
                        const h = teams.find((t) => t.team === game.homeTeam);
                        const a = teams.find((t) => t.team === game.awayTeam);

                        const mainColor = SWAP_COLORS.has(game.homeTeam)
                            ? h?.alternateColor
                            : h?.color || "white";
                        const altColor = SWAP_COLORS.has(game.homeTeam)
                            ? h?.color || "white"
                            : h?.alternateColor || "#007ac2";

                        const mainColora = a
                            ? SWAP_COLORS.has(game.awayTeam)
                                ? a.alternateColor
                                : a?.color || "white"
                            : "white";
                        const altColora = a
                            ? SWAP_COLORS.has(game.awayTeam)
                                ? a?.color || "white"
                                : a.alternateColor
                            : "#007ac2";

                        const hselected =
                            (game.completed || game.manual) && game.homePoints > game.awayPoints;
                        const aselected =
                            (game.completed || game.manual) && game.awayPoints > game.homePoints;

                        const awayProb = Math.min(
                            Math.max((100 - (game.probability || 0.5) * 100).toFixed(0), 1),
                            99
                        );
                        const homeProb = Math.max(
                            Math.min(((game.probability || 0.5) * 100).toFixed(0), 99),
                            1
                        );

                        if (excitementSortedGames.filter(g => !g.game.manual).length > index +1){
                            return null;
                        }

                        return (
                            <div key={game.id || index} className={`scenario-matchup ${game.manual ? "" : "selection-matchup"}`}>
                                {!game.manual && (
                                    <div className="scenario-date">
                                        {game.week == 14 ? "Conf. Champ" : `Week ${game.week}`}
                                    </div>
                                )}
                                <div className="scenario-odds">
                                    {awayProb}%
                                </div>

                                {/* Away Team */}
                                <div
                                    className="scenario-team"
                                    onClick={() =>
                                        aselected ? change(game.id, null) : change(game.id, "away")
                                    }
                                    style={{
                                        filter: hselected ? "grayscale(1) contrast(0.5)" : "none",
                                        ...(aselected && { boxShadow: `0 0 0 2px ${altColora} inset` }),
                                        borderColor: aselected ? mainColora : "transparent",
                                        background: aselected
                                            ? `linear-gradient(45deg, color-mix(in srgb, ${mainColora} 5%, white) 0%, color-mix(in srgb, ${mainColora} 85%, black) 18%, color-mix(in srgb, ${mainColora} 65%, white) 38%, color-mix(in srgb, ${mainColora} 90%, black) 58%, color-mix(in srgb, ${mainColora} 70%, black) 78%, color-mix(in srgb, ${mainColora} 45%, white) 100%)`
                                            : "var(--text)"
                                    }}
                                >
                                    <img
                                        style={{
                                            filter:
                                                WHITE_LOGO.has(a?.team) && aselected
                                                    ? "brightness(0) invert(1)"
                                                    : "none"
                                        }}
                                        src={
                                            entry.loss === "FCS"
                                                ? "fcs.png"
                                                : getTeamLogoUrl(
                                                    listOfTeams[a?.team],
                                                    REG_LOGO.has(a?.team) ||
                                                    WHITE_LOGO.has(a?.team) ||
                                                    !aselected
                                                )
                                        }
                                        alt={a?.team || game.awayTeam}
                                    />
                                    <div className="scenario-team-name">
                                        {a?.team || game.awayTeam}
                                    </div>
                                </div>

                                <div className="scenario-matchup-vs">
                                    {game.neutralSite
                                        ? `VS`
                                        : "@"}
                                </div>

                                {/* Home Team */}
                                <div
                                    className="scenario-team"
                                    onClick={() =>
                                        hselected ? change(game.id, null) : change(game.id, "home")
                                    }
                                    style={{
                                        filter: aselected ? "grayscale(1) contrast(0.5)" : "none",

                                        ...(hselected && { boxShadow: `0 0 0 2px ${altColor} inset` }),

                                        borderColor: hselected ? mainColor : "transparent",
                                        background: hselected
                                            ? `linear-gradient(45deg, color-mix(in srgb, ${mainColor} 5%, white) 0%, color-mix(in srgb, ${mainColor} 85%, black) 18%, color-mix(in srgb, ${mainColor} 65%, white) 38%, color-mix(in srgb, ${mainColor} 90%, black) 58%, color-mix(in srgb, ${mainColor} 70%, black) 78%, color-mix(in srgb, ${mainColor} 45%, white) 100%)`
                                            : "var(--text)"
                                    }}
                                >
                                    <img
                                        style={{
                                            filter:
                                                WHITE_LOGO.has(h?.team) && hselected
                                                    ? "brightness(0) invert(1)"
                                                    : "none"
                                        }}
                                        src={getTeamLogoUrl(
                                            listOfTeams[h?.team],
                                            REG_LOGO.has(h?.team) ||
                                            WHITE_LOGO.has(h?.team) ||
                                            !hselected
                                        )}
                                        alt={h?.team || game.homeTeam}
                                    />
                                    <div className="scenario-team-name">
                                        {h?.team || game.homeTeam}
                                    </div>
                                </div>

                                <div className="scenario-odds">
                                    {homeProb}%
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <div id="scenario-menu">
                {step == 1 && selectedTeams.length == 0 && selectedWeeks.length == 0 &&
                    <div className="tab-group">
                        <div className={`tab left ${filterType == "teams" && "active"}`} onClick={() => setFilterType("teams")} >Select Teams</div>

                        <div className={`tab right ${filterType == "weeks" && "active"}`} onClick={() => setFilterType("weeks")}>Select Weeks</div>

                    </div>
                }
                {step == 1 && selectedTeams.length > 0 && <><div className="slim-butt"
                    onClick={() => {
                        setStep(2);
                        document.querySelector('#scenario-details')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                ><PencilSparkles size={24} strokeWidth={2} /> &nbsp; Make Predictions</div>
                    <div className="slim-note fixed-mid">{selectedTeams.length} Teams Selected</div>
                </>}
                {step == 1 && selectedWeeks.length > 0 && <><div className="slim-butt"
                    onClick={() => {
                        setStep(2);
                        document.querySelector('#scenario-details')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                ><PencilSparkles size={24} strokeWidth={2} /> &nbsp; Make Predictions</div>
                    <div className="slim-note fixed-mid">{selectedWeeks.length} Weeks Selected</div>
                </>}
                {step == 2 && <>
                    <div className="slim-butt"
                        onClick={() => {
                            simResults.runSimulation?.(250);
                            setMSwitch(false);
                        }}
                    ><FlaskConical size={24} strokeWidth={2} /> &nbsp; Run Simulations</div>
                    <div className="slim-not fixed-mid">{excitementSortedGames.filter(g => g.game.manual).length} Games Modified</div>
                </>}
            </div>
        </div>
    );
};

export default memo(ScenarioEditor);